import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

const VoiceAISalesCoach = ({ onSearchRequest, agents = [], className = '' }) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'coach',
      text: 'Hi! I\'m your Coach. Start with one of the common questions or describe what you\'re looking for.',
      timestamp: new Date()
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Quick search configurations
  const quickSearches = [
    {
      id: 'failed-calls',
      label: '❌ Recent Failed Calls',
      description: 'Show calls that didn\'t reach the target',
      filters: {
        call_successful: 'false',
        limit: '100'
      }
    },
    {
      id: 'today-calls',
      label: '📅 Calls from Today',
      description: 'All calls made today',
      filters: {
        // This will be handled with date logic
        dateRange: 'today',
        limit: '100'
      }
    },
    {
      id: 'positive-sentiment',
      label: '😊 Positive Sentiment Calls',
      description: 'Calls with positive user feedback',
      filters: {
        user_sentiment: 'Positive',
        limit: '50'
      }
    },
    {
      id: 'negative-sentiment',
      label: '😞 Negative Sentiment Calls',
      description: 'Calls with negative user feedback',
      filters: {
        user_sentiment: 'Negative',
        limit: '50'
      }
    },
    {
      id: 'no-answer',
      label: '📞 No Answer / Busy',
      description: 'Calls that went unanswered or were busy',
      filters: {
        disconnection_reason: 'dial_no_answer',
        limit: '100'
      }
    },
    {
      id: 'successful-calls',
      label: '✅ Successful ICP Reaches',
      description: 'Calls that successfully reached ICP',
      filters: {
        call_successful: 'true',
        limit: '50'
      }
    },
    {
      id: 'outbound-calls',
      label: '📤 Outbound Calls',
      description: 'All outbound calls',
      filters: {
        direction: 'outbound',
        limit: '100'
      }
    },
    {
      id: 'inbound-calls',
      label: '📥  Inbound Calls',
      description: 'All inbound calls',
      filters: {
        direction: 'inbound',
        limit: '100'
      }
    },
    {
      id: 'errors',
      label: '⚠️ Error Calls',
      description: 'Calls with technical errors',
      filters: {
        call_status: 'error',
        limit: '100'
      }
    }
  ];

  const handleQuickSearch = (search) => {
    // Add user message
    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: search.label,
      timestamp: new Date()
    };

    // Add coach response
    const coachMessage = {
      id: Date.now() + 1,
      type: 'coach',
      text: `Searching for: ${search.description}`,
      timestamp: new Date(),
      action: search
    };

    setMessages(prev => [...prev, userMessage, coachMessage]);

    // Trigger the search
    if (onSearchRequest) {
      onSearchRequest(search.filters);
    }
  };

  const handleCustomSearch = (e) => {
    e.preventDefault();
    if (!inputText.trim()) return;

    // Add user message
    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: inputText,
      timestamp: new Date()
    };

    // Process the custom search (basic keyword matching)
    const filters = parseCustomSearch(inputText.toLowerCase());

    // Add coach response
    const coachMessage = {
      id: Date.now() + 1,
      type: 'coach',
      text: filters ? `I found a search pattern! Applying filters...` : `Can't help you with that just yet. You know private beta and all! How about you try the quick search buttons for more specific results, Champ!`,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage, coachMessage]);

    // Trigger search if filters were found
    if (filters && onSearchRequest) {
      onSearchRequest(filters);
    }

    setInputText('');
  };

  const parseCustomSearch = (text) => {
    const filters = {};

    // Detect patterns in user input
    if (text.includes('failed') || text.includes('unsuccessful')) {
      filters.call_successful = 'false';
    }
    if (text.includes('successful') || text.includes('success')) {
      filters.call_successful = 'true';
    }
    if (text.includes('positive')) {
      filters.user_sentiment = 'Positive';
    }
    if (text.includes('negative')) {
      filters.user_sentiment = 'Negative';
    }
    if (text.includes('outbound')) {
      filters.direction = 'outbound';
    }
    if (text.includes('inbound')) {
      filters.direction = 'inbound';
    }
    if (text.includes('today')) {
      filters.dateRange = 'today';
    }
    if (text.includes('error')) {
      filters.call_status = 'error';
    }
    if (text.includes('no answer') || text.includes('unanswered')) {
      filters.disconnection_reason = 'dial_no_answer';
    }
    if (text.includes('busy')) {
      filters.disconnection_reason = 'dial_busy';
    }

    // Check for agent names
    const agentMatch = agents.find(agent =>
      text.includes(agent.agent_name?.toLowerCase())
    );
    if (agentMatch) {
      filters.agent_id = agentMatch.agent_id;
    }

    return Object.keys(filters).length > 0 ? { ...filters, limit: '100' } : null;
  };

  const clearChat = () => {
    setMessages([
      {
        id: 1,
        type: 'coach',
        text: 'Chat cleared! How can I help you search for calls?',
        timestamp: new Date()
      }
    ]);
  };

  return (
    <div className={`card box-shadow border-0 mb-0 ${className}`} style={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* Floating Coach Avatar */}
      <img
        src="/dist/assets/images/ankr-voiceai-coach.webp"
        alt="Ankr AI Coach"
        style={{ position: 'absolute', top: -40, right: 10, width: 120, height: 120, zIndex: 10, borderRadius: '50%', boxShadow: '0 4px 8px rgba(0,0,0,0.2)', cursor: 'pointer' }}
        onClick={() => setIsCollapsed(!isCollapsed)}
        title={isCollapsed ? "Expand coach" : "Collapse coach"}
      />

      {/* Header */}
      <div className="card-header d-flex align-items-center gap-2" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderBottom: '2px solid rgba(255,255,255,0.2)', borderRadius: '10px 10px 0 0' }}>
        <h6 className="text-white mb-0" style={{ fontWeight: 600, letterSpacing: '0.3px' }}>Voice AI Sales Coach</h6>
        <button className="btn btn-sm text-white btn-outline-light" onClick={() => setIsCollapsed(!isCollapsed)} title={isCollapsed ? "Expand" : "Collapse"} style={{ borderColor: 'rgba(255,255,255,0.5)' }}>
          <i className={`fas fa-chevron-${isCollapsed ? 'up' : 'down'}`}></i>
        </button>
      </div>

      {!isCollapsed && (
        <>
          {/* Chat Messages */}
          <div className="card-body border-1" style={{ flex: 1, overflowY: 'auto', maxHeight: '500px' }}>
            <div className="d-flex flex-column gap-3">
              {messages.map((message) => (
                <div key={message.id} className={`d-flex ${message.type === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
                  <div className={`p-3 rounded ${message.type === 'user' ? 'bg-primary text-white' : 'bg-light border'}`} style={{ maxWidth: '80%' }}>
                    <div className="d-flex align-items-center mb-1">
                      <small className={`fw-bold ${message.type === 'coach' ? 'text-primary' : 'text-white'}`}>
                        {message.type === 'coach' ? 'Coach' : 'You'}
                      </small>
                    </div>
                    <div>{message.text}</div>
                    <small className={message.type === 'user' ? 'text-white' : 'text-muted'} style={{ fontSize: '0.7rem' }}>
                      {message.timestamp.toLocaleTimeString()}
                    </small>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Search Buttons */}
            {messages.length <= 2 && (
              <div className="mt-4">
                <small className="text-muted fw-bold">Quick Searches:</small>
                <div className="row g-2 mt-2">
                  {quickSearches.map((search) => (
                    <div key={search.id} className="col-md-6">
                      <button className="btn btn-outline-primary btn-sm w-100 text-start d-flex align-items-center" onClick={() => handleQuickSearch(search)} title={search.description}>
                        <div className="flex-grow-1">
                          <div style={{ fontSize: '0.85rem' }}>{search.label}</div>
                          <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>{search.description}</small>
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Minimized Quick Search */}
            {messages.length > 2 && (
              <div className="mt-3">
                <details>
                  <summary className="text-primary" style={{ cursor: 'pointer', fontSize: '0.9rem' }}>
                    <i className="fas fa-search me-1"></i>Show Quick Searches
                  </summary>
                  <div className="row g-2 mt-2">
                    {quickSearches.map((search) => (
                      <div key={search.id} className="col-md-6">
                        <button className="btn btn-outline-secondary btn-sm w-100 text-start" onClick={() => handleQuickSearch(search)} title={search.description}>
                          <span style={{ fontSize: '0.8rem' }}>{search.label}</span>
                        </button>
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            )}
          </div>

          {/* Input Area */}
          <div className="card-footer bg-light">
            <form onSubmit={handleCustomSearch}>
              <div className="input-group">
                <input type="text" className="form-control" placeholder="" value={inputText} onChange={(e) => setInputText(e.target.value)} />
                <button className="btn btn-primary" type="submit" disabled={!inputText.trim()}>
                  <i className="fas fa-paper-plane"></i>
                </button>
                <button className="btn btn-sm btn-outline-secondary" onClick={clearChat} title="Clear chat">
                  <i className="fas fa-trash"></i>
                </button>
              </div>
              <small className="text-muted d-block mt-1">
                💡 Tip: Try phrases like &quot;failed calls&quot;, &quot;positive sentiment&quot;, &quot;outbound from [agent name]&quot;, &quot;calls today&quot;
              </small>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

VoiceAISalesCoach.propTypes = {
  onSearchRequest: PropTypes.func.isRequired,
  agents: PropTypes.arrayOf(
    PropTypes.shape({
      agent_id: PropTypes.string,
      agent_name: PropTypes.string
    })
  ),
  className: PropTypes.string
};

export default VoiceAISalesCoach;